import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';

const AssessmentTakePage = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assessments/${assessmentId}/take`);
      const saved = {};
      (res.data.saved_answers || []).forEach((a) => { saved[a.question_id] = a.selected_option_id; });
      setAnswers(saved);
      setIndex(Math.min(res.data.last_question_index || 0, (res.data.questions?.length || 1) - 1));
      setData(res.data);
    } catch (error) {
      setBlocked(error.response?.data?.detail || 'This assessment could not be opened.');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  const questions = data?.questions || [];
  const question = questions[index];
  const answeredCount = Object.keys(answers).length;
  const allAnswered = questions.length > 0 && answeredCount === questions.length;

  const choose = async (optionId) => {
    setAnswers((prev) => ({ ...prev, [question.id]: optionId }));
    try {
      await axios.post(`${API}/assessments/attempts/${data.attempt_id}/answer`, {
        question_id: question.id,
        selected_option_id: optionId,
        last_question_index: index,
      });
    } catch (error) {
      toast.error('That answer was not saved — check your connection');
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        answers: questions.map((q) => ({ question_id: q.id, selected_option_id: answers[q.id] })),
      };
      const res = await axios.post(`${API}/assessments/${assessmentId}/submit`, payload);
      navigate(`/dashboard/assessments/results/${res.data.attempt_id}${res.data.is_first_completed ? '?first=1' : ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Your assessment could not be submitted');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-20 text-gray-300" data-testid="assessment-loading">Opening your assessment…</div>;
  }

  if (blocked) {
    return (
      <div className="max-w-2xl mx-auto py-16">
        <Card data-testid="assessment-blocked">
          <CardContent className="p-8 text-center space-y-4">
            <p className="text-gray-700">{blocked}</p>
            <Button variant="outline" onClick={() => navigate('/dashboard/modules')} data-testid="assessment-blocked-back">
              Back to the Coaching Program
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-16" data-testid="assessment-take-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{data.location_label || 'Assessment'}</h1>
        {data.instructions && (
          <p className="text-gray-300 whitespace-pre-line" data-testid="assessment-instructions">{data.instructions}</p>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-gray-300 mb-2">
          <span data-testid="assessment-progress-label">Question {index + 1} of {questions.length}</span>
          <span>{answeredCount} answered</span>
        </div>
        <Progress value={((index + 1) / questions.length) * 100} className="h-2" />
      </div>

      <Card data-testid="assessment-question-card">
        <CardContent className="p-6 sm:p-8">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-6" data-testid="assessment-question-text">
            {question.text}
          </h2>
          <div className="space-y-3">
            {question.answers.map((option) => {
              const selected = answers[question.id] === option.option_id;
              return (
                <button
                  key={option.option_id}
                  onClick={() => choose(option.option_id)}
                  data-testid={`assessment-option-${option.option_id}`}
                  className={`w-full text-left rounded-xl border-2 px-5 py-4 transition-colors duration-200 flex items-center justify-between gap-4 ${
                    selected
                      ? 'border-cyan-500 bg-cyan-50 text-gray-900'
                      : 'border-gray-200 bg-white hover:border-cyan-300 text-gray-700'
                  }`}
                >
                  <span className="text-sm md:text-base">{option.label}</span>
                  {selected && <Check className="w-5 h-5 text-cyan-600 shrink-0" />}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between mt-6 gap-3">
        <Button
          variant="outline"
          className="bg-white"
          disabled={index === 0}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          data-testid="assessment-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        {index < questions.length - 1 ? (
          <Button
            className="bg-gradient-to-r from-cyan-500 to-purple-500 text-white"
            disabled={!answers[question.id]}
            onClick={() => setIndex((i) => Math.min(questions.length - 1, i + 1))}
            data-testid="assessment-next-btn"
          >
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
            disabled={!allAnswered || submitting}
            onClick={submit}
            data-testid="assessment-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Submit Assessment
          </Button>
        )}
      </div>

      {index === questions.length - 1 && !allAnswered && (
        <p className="text-center text-sm text-amber-300 mt-4" data-testid="assessment-incomplete-notice">
          Every question needs an answer before you can submit — {questions.length - answeredCount} left.
        </p>
      )}

      <p className="text-center text-xs text-gray-400 mt-8">
        Your progress saves as you go. You can close this and come back on any device.
      </p>
    </div>
  );
};

export default AssessmentTakePage;
